import { useQuery } from '@tanstack/react-query';
import { Box, Headphones, type LucideIcon, Router, Wifi } from 'lucide-react';
import { catalogApi } from '@/api/commercial/catalog';
import {
  fmtCents,
  ResumeCard,
  ResumeCardHeader,
} from '@/components/commercial/proposals/shared/summary-components';
import { formatDataAmount } from '@/lib/formatters';
import {
  type ICatalogItem,
  type IProposalCartItem,
  NegotiationType,
} from '@/types/commercial/cart';
import { getNegotiationLabel } from '@/utils/commercial/cart-item-grouping';
import { originalOrUnit, subtotalOrDiscountedFallback } from '@/utils/commercial/cart-price';

interface ResumeItemsSummaryCardProps {
  items: IProposalCartItem[];
  proposalId?: string;
  paymentType?: 'CASH' | 'INSTALLMENTS';
}

interface PriceSummary {
  unitInCents: number;
  grossInCents: number;
  netInCents: number;
  discountPercent: number;
}

interface SummaryRow {
  key: string;
  cells: React.ReactNode[];
  price: PriceSummary;
}

interface CategoryTableProps {
  label: string;
  icon: LucideIcon;
  colorClass: string;
  bgClass: string;
  headers: string[];
  rows: SummaryRow[];
}

const PRODUCT_TYPES = [NegotiationType.SALE_WITH_SIM_CARD, NegotiationType.SALE_WITHOUT_SIM_CARD];

function getCapexPrice(item: IProposalCartItem, isCash: boolean): PriceSummary {
  const unitInCents = isCash
    ? originalOrUnit(item.originalPriceCashInCents, item.unitPriceCashInCents)
    : originalOrUnit(item.originalPriceInstallmentInCents, item.unitPriceInstallmentInCents);
  const netInCents = subtotalOrDiscountedFallback(
    isCash ? item.subtotalCashInCents : item.subtotalInstallmentInCents,
    unitInCents,
    item.quantity,
    item.discountPercent,
  );

  return {
    unitInCents,
    grossInCents: unitInCents * item.quantity,
    netInCents,
    discountPercent: item.discountPercent,
  };
}

function getRecurringPrice(
  item: IProposalCartItem,
  originalInCents: number,
  unitInCents: number,
  subtotalInCents: number,
  discountPercent: number,
): PriceSummary {
  const originalUnitInCents = originalOrUnit(originalInCents, unitInCents);

  return {
    unitInCents: originalUnitInCents,
    grossInCents: originalUnitInCents * item.quantity,
    netInCents: subtotalOrDiscountedFallback(
      subtotalInCents,
      originalUnitInCents,
      item.quantity,
      discountPercent,
    ),
    discountPercent,
  };
}

function PriceCells({ price }: { price: PriceSummary }) {
  const hasDiscount = price.discountPercent > 0;

  return (
    <>
      <td className="whitespace-nowrap px-3 py-3.5 text-right text-xs tabular-nums text-gray-700">
        {fmtCents(price.unitInCents)}
      </td>
      <td className="whitespace-nowrap px-3 py-3.5 text-right text-xs font-medium tabular-nums text-gray-900">
        <span className={hasDiscount ? 'text-gray-500 line-through' : undefined}>
          {fmtCents(price.grossInCents)}
        </span>
      </td>
      <td className="whitespace-nowrap px-3 py-3.5 text-right text-xs tabular-nums">
        {hasDiscount ? (
          <span className="font-semibold text-green-600">-{price.discountPercent}%</span>
        ) : (
          <span className="text-gray-400">-</span>
        )}
      </td>
      <td className="whitespace-nowrap px-3 py-3.5 text-right text-xs tabular-nums">
        {hasDiscount ? (
          <span className="font-bold text-green-600">{fmtCents(price.netInCents)}</span>
        ) : (
          <span className="text-gray-400">-</span>
        )}
      </td>
    </>
  );
}

function CategoryTable({
  label,
  icon: Icon,
  colorClass,
  bgClass,
  headers,
  rows,
}: CategoryTableProps) {
  if (rows.length === 0) return null;

  const subtotalInCents = rows.reduce((total, row) => total + row.price.netInCents, 0);

  return (
    <ResumeCard className="overflow-hidden">
      <ResumeCardHeader
        title={label}
        subtitle={`${rows.length} ${rows.length === 1 ? 'item' : 'itens'}`}
        icon={
          <div
            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${bgClass}`}
          >
            <Icon className={`h-4.5 w-4.5 ${colorClass}`} />
          </div>
        }
      />

      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50/40">
              {headers.map((header, index) => (
                <th
                  key={header}
                  className={`px-3 py-3 text-[10px] font-bold uppercase leading-4 tracking-wide text-gray-500 ${
                    index === 0
                      ? 'text-left'
                      : index >= headers.length - 4
                        ? 'text-right'
                        : 'text-center'
                  }`}
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((row) => (
              <tr key={row.key} className="transition-colors hover:bg-gray-50/60">
                {row.cells.map((cell, index) => (
                  <td
                    key={`${row.key}-${headers[index]}`}
                    className={`px-3 py-3.5 text-xs text-gray-700 ${
                      index === 0
                        ? 'break-words text-left font-semibold text-gray-950'
                        : 'break-words text-center'
                    }`}
                  >
                    {cell}
                  </td>
                ))}
                <PriceCells price={row.price} />
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-gray-100 bg-gray-50/40">
              <td
                colSpan={headers.length - 1}
                className="px-3 py-3.5 text-right text-xs font-bold text-gray-700"
              >
                Subtotal de {label.toLowerCase()}
              </td>
              <td className="whitespace-nowrap px-3 py-3.5 text-right text-sm font-bold tabular-nums text-gray-950">
                {fmtCents(subtotalInCents)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </ResumeCard>
  );
}

function getServiceRows(item: IProposalCartItem, name: string): SummaryRow[] {
  const dimensions = [
    {
      key: 'monthly',
      label: 'Mensal',
      original: item.originalPriceMonthlyInCents,
      unit: item.unitPriceMonthlyInCents,
      subtotal: item.subtotalMonthlyInCents,
    },
    {
      key: 'yearly',
      label: 'Anual',
      original: item.originalPriceYearlyInCents,
      unit: item.unitPriceYearlyInCents,
      subtotal: item.subtotalYearlyInCents,
    },
    {
      key: 'fixed',
      label: 'Fixo',
      original: item.originalPriceFixedInCents,
      unit: item.unitPriceFixedInCents,
      subtotal: item.subtotalFixedInCents,
    },
  ].filter(({ original, unit, subtotal }) => original > 0 || unit > 0 || subtotal > 0);

  return dimensions.map((dimension) => ({
    key: `${item.id}-${dimension.key}`,
    cells: [name, item.quantity, dimension.label],
    price: getRecurringPrice(
      item,
      dimension.original,
      dimension.unit,
      dimension.subtotal,
      item.discountPercent,
    ),
  }));
}

export function ResumeItemsSummaryCard({
  items,
  proposalId,
  paymentType,
}: ResumeItemsSummaryCardProps) {
  const catalogResults = useQuery({
    queryKey: ['catalog-all'],
    queryFn: async () => {
      const types: NegotiationType[] = [
        NegotiationType.SALE_WITH_SIM_CARD,
        NegotiationType.SALE_WITHOUT_SIM_CARD,
        NegotiationType.SIM_CARD,
        NegotiationType.ACCESSORY_SALE,
        NegotiationType.SERVICES,
      ];
      const results = await Promise.all(
        types.map((negotiationType) => catalogApi.findMany({ negotiationType })),
      );
      return new Map<string, ICatalogItem>(
        results.flatMap((catalog) => catalog.map((item) => [item.id, item])),
      );
    },
    enabled: !!proposalId,
  });

  const catalogLookup = catalogResults.data ?? new Map<string, ICatalogItem>();
  const isCash = paymentType !== 'INSTALLMENTS';
  const getName = (item: IProposalCartItem) =>
    catalogLookup.get(item.productId)?.name ?? item.productId;

  const productRows = items
    .filter((item) => PRODUCT_TYPES.includes(item.negotiationType))
    .map((item) => ({
      key: item.id,
      cells: [
        getName(item),
        (catalogLookup.get(item.productId)?.description ?? item.productDescription)?.trim() || '—',
        item.quantity,
        getNegotiationLabel(item.negotiationType),
      ],
      price: getCapexPrice(item, isCash),
    }));

  const accessoryRows = items
    .filter((item) => item.negotiationType === NegotiationType.ACCESSORY_SALE)
    .map((item) => ({
      key: item.id,
      cells: [getName(item), item.quantity],
      price: getCapexPrice(item, isCash),
    }));

  const connectivityRows = items
    .filter(
      (item) =>
        item.negotiationType === NegotiationType.SALE_WITH_SIM_CARD ||
        item.negotiationType === NegotiationType.SIM_CARD,
    )
    .map((item) => {
      const connectivity = item.connectivityDetails;
      const combo = connectivity
        ? [formatDataAmount(connectivity.dataAmountMb), connectivity.planType]
            .filter(Boolean)
            .join(' - ')
        : '-';
      const discountPercent =
        item.negotiationType === NegotiationType.SALE_WITH_SIM_CARD
          ? item.connectivityDiscountPercent
          : item.discountPercent;

      return {
        key: `${item.id}-connectivity`,
        cells: [connectivity?.carrier ?? '-', item.quantity, combo],
        price: getRecurringPrice(
          item,
          item.originalPriceMonthlyInCents,
          item.unitPriceMonthlyInCents,
          item.subtotalMonthlyInCents,
          discountPercent,
        ),
      };
    });

  const serviceRows = items
    .filter((item) => item.negotiationType === NegotiationType.SERVICES)
    .flatMap((item) => getServiceRows(item, getName(item)));

  if (items.length === 0) {
    return (
      <ResumeCard>
        <ResumeCardHeader title="Resumo dos itens" />
        <div className="py-10 text-center text-sm text-muted-foreground">
          Nenhum item adicionado
        </div>
      </ResumeCard>
    );
  }

  return (
    <section>
      <div className="mb-4">
        <h3 className="text-sm font-bold text-gray-900">Resumo dos itens</h3>
      </div>

      <div className="space-y-4">
        <CategoryTable
          label="Produtos"
          icon={Box}
          colorClass="text-blue-600"
          bgClass="bg-blue-50"
          headers={[
            'Item',
            'Descrição',
            'Quantidade',
            'Tipo',
            'Valor unitário',
            'Valor total',
            'Desconto',
            'Valor com desconto',
          ]}
          rows={productRows}
        />
        <CategoryTable
          label="Acessórios"
          icon={Router}
          colorClass="text-emerald-600"
          bgClass="bg-emerald-50"
          headers={[
            'Item',
            'Quantidade',
            'Valor unitário',
            'Valor total',
            'Desconto',
            'Valor com desconto',
          ]}
          rows={accessoryRows}
        />
        <CategoryTable
          label="Conectividade"
          icon={Wifi}
          colorClass="text-violet-600"
          bgClass="bg-violet-50"
          headers={[
            'Operadora',
            'Quantidade',
            'Combo',
            'Valor mensal unitário',
            'Valor total',
            'Desconto',
            'Valor com desconto',
          ]}
          rows={connectivityRows}
        />
        <CategoryTable
          label="Serviços"
          icon={Headphones}
          colorClass="text-amber-600"
          bgClass="bg-amber-50"
          headers={[
            'Item',
            'Quantidade',
            'Pagamento',
            'Valor unitário',
            'Valor total',
            'Desconto',
            'Valor com desconto',
          ]}
          rows={serviceRows}
        />
      </div>
    </section>
  );
}
